import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatNaira } from '@/lib/utils';
import { CaretLeft, Plus, Check, Clock, Trash, ListChecks } from '@phosphor-icons/react';

export default function MilestonesPage() {
  const { jobId } = useParams();
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState([]);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [newMs, setNewMs] = useState([{ description: '', percentage: 50 }, { description: '', percentage: 50 }]);

  useEffect(() => {
    const load = async () => {
      try {
        const [msRes, jobRes] = await Promise.all([
          api.get(`/milestones/${jobId}`),
          api.get(`/jobs/${jobId}`)
        ]);
        setMilestones(msRes.data.milestones || []);
        setJob(jobRes.data.job);
      } catch {} finally { setLoading(false); }
    };
    load();
  }, [jobId, api]);

  const addRow = () => setNewMs(ms => [...ms, { description: '', percentage: 0 }]);
  const removeRow = (i) => setNewMs(ms => ms.filter((_, idx) => idx !== i));
  const updateRow = (i, field, val) => setNewMs(ms => ms.map((m, idx) => idx === i ? { ...m, [field]: val } : m));
  const totalPct = newMs.reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0);

  const setupMilestones = async () => {
    try {
      await api.post('/milestones/setup', { job_id: jobId, milestones: newMs.map(m => ({ description: m.description, percentage: parseFloat(m.percentage) || 0 })) });
      setShowSetup(false);
      const { data } = await api.get(`/milestones/${jobId}`);
      setMilestones(data.milestones || []);
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const completeMilestone = async (msId) => {
    try { await api.post(`/milestones/${msId}/complete`); window.location.reload(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const approveMilestone = async (msId) => {
    try { await api.post(`/milestones/${msId}/approve`); window.location.reload(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const isOwner = user && job?.customer_id === user.id;
  const acceptedBid = job?.accepted_bid_id;

  if (loading) return <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#D4C5B3] border-t-[#E86A33] rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] pb-10 max-w-[480px] mx-auto" data-testid="milestones-page">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/job/${jobId}`)} className="w-9 h-9 bg-[#E8DDD2] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
        <h1 className="font-heading text-lg font-bold text-[#1C1C1C] flex-1">Milestones</h1>
        <span className="text-xs font-bold text-[#5C5C5C] bg-[#E8DDD2] px-2 py-1 rounded-full">{milestones.filter(m => m.status === 'approved').length}/{milestones.length}</span>
      </header>

      <div className="p-4 space-y-4">
        {/* Progress bar */}
        {milestones.length > 0 && (
          <div className="bg-white border border-[#D4C5B3] rounded-xl p-4">
            <div className="flex justify-between text-xs text-[#5C5C5C] mb-2">
              <span className="font-bold">Progress</span>
              <span>{Math.round((milestones.filter(m => m.status === 'approved').length / milestones.length) * 100)}%</span>
            </div>
            <div className="w-full h-3 bg-[#E5E3DB] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#1A4D2E] to-[#2D6A4F] rounded-full transition-all" style={{ width: `${(milestones.filter(m => m.status === 'approved').length / milestones.length) * 100}%` }} />
            </div>
          </div>
        )}

        {/* Milestones list */}
        {milestones.length === 0 ? (
          <div className="text-center py-10">
            <ListChecks size={48} className="text-[#E5E3DB] mx-auto mb-4" />
            <h3 className="font-heading text-lg font-bold text-[#1C1C1C] mb-2">No milestones set</h3>
            <p className="text-sm text-[#5C5C5C] mb-4 font-body">Break the job into milestones for staged payments.</p>
            {isOwner && acceptedBid && (
              <button onClick={() => setShowSetup(true)} className="bg-[#1A4D2E] text-white font-bold px-6 py-3 rounded-xl font-body" data-testid="setup-milestones-button">Setup Milestones</button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {milestones.map((ms, i) => (
              <div key={ms.id} className={`bg-white border rounded-xl p-4 ${ms.status === 'approved' ? 'border-[#2D6A4F] bg-[#2D6A4F]/5' : ms.status === 'completed' ? 'border-[#F4A261]' : 'border-[#D4C5B3]'}`} data-testid={`milestone-${ms.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${ms.status === 'approved' ? 'bg-[#2D6A4F] text-white' : ms.status === 'completed' ? 'bg-[#F4A261] text-white' : 'bg-[#E8DDD2] text-[#5C5C5C]'}`}>
                    {ms.status === 'approved' ? <Check size={18} /> : ms.index}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-[#1C1C1C] font-body">{ms.description}</h4>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-[#5C5C5C]">{ms.percentage}%</span>
                      <span className="text-xs font-bold text-[#E86A33]">{formatNaira(ms.amount)}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ms.status === 'approved' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : ms.status === 'completed' ? 'bg-[#F4A261]/10 text-[#F4A261]' : 'bg-[#E8DDD2] text-[#5C5C5C]'}`}>
                        {ms.status}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Artisan can mark complete */}
                {!isOwner && ms.status === 'pending' && (
                  <button onClick={() => completeMilestone(ms.id)} className="w-full mt-3 bg-[#F4A261] text-white font-bold py-2 rounded-lg text-xs font-body" data-testid={`complete-ms-${ms.id}`}>Mark as Completed</button>
                )}
                {/* Customer can approve */}
                {isOwner && ms.status === 'completed' && (
                  <button onClick={() => approveMilestone(ms.id)} className="w-full mt-3 bg-[#2D6A4F] text-white font-bold py-2 rounded-lg text-xs font-body flex items-center justify-center gap-1" data-testid={`approve-ms-${ms.id}`}><Check size={14} /> Approve & Release Payment</button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Setup Form */}
        {showSetup && (
          <div className="bg-white border-2 border-[#1A4D2E] rounded-xl p-5" data-testid="milestone-setup-form">
            <h3 className="font-heading text-lg font-bold text-[#1C1C1C] mb-3">Setup Milestones</h3>
            <div className="space-y-3">
              {newMs.map((m, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#1A4D2E] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-1">{i + 1}</div>
                  <div className="flex-1 space-y-2">
                    <input value={m.description} onChange={e => updateRow(i, 'description', e.target.value)} placeholder={`Milestone ${i + 1} description`} className="w-full bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] border border-[#D4C5B3] rounded-lg px-3 py-2 text-sm font-body outline-none" data-testid={`ms-desc-${i}`} />
                    <div className="flex gap-2 items-center">
                      <input type="number" value={m.percentage} onChange={e => updateRow(i, 'percentage', e.target.value)} className="w-20 bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] border border-[#D4C5B3] rounded-lg px-3 py-2 text-sm font-body outline-none text-center" data-testid={`ms-pct-${i}`} />
                      <span className="text-xs text-[#5C5C5C]">%</span>
                      {newMs.length > 2 && <button onClick={() => removeRow(i)} className="text-red-400 hover:text-red-600"><Trash size={16} /></button>}
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={addRow} className="w-full border border-dashed border-[#D4C5B3] rounded-lg py-2 text-xs font-bold text-[#5C5C5C] flex items-center justify-center gap-1"><Plus size={14} /> Add Milestone</button>
              <div className={`text-center text-sm font-bold ${Math.abs(totalPct - 100) < 0.01 ? 'text-[#2D6A4F]' : 'text-red-500'}`}>Total: {totalPct}% {Math.abs(totalPct - 100) < 0.01 ? '✓' : '(must be 100%)'}</div>
              <div className="flex gap-3">
                <button onClick={() => setShowSetup(false)} className="flex-1 bg-[#E8DDD2] text-[#5C5C5C] font-bold py-3 rounded-lg text-sm">Cancel</button>
                <button onClick={setupMilestones} disabled={Math.abs(totalPct - 100) > 0.01} className="flex-1 bg-[#1A4D2E] text-white font-bold py-3 rounded-lg text-sm disabled:opacity-50" data-testid="save-milestones-button">Save Milestones</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
